import { inject, Injectable } from '@angular/core';
import { Account } from '@mkp/account/data-access';
import { CompanyProfile } from '@mkp/company/models';
import { LegalEntityDto } from '@mkp/legal-entity/data-access';
import { LocationDto } from '@mkp/location/models';
import { toUUID, WalletDto } from '@mkp/shared/models';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { OnboardingApiService } from './onboarding-api.service';
import { OnboardingService } from './onboarding.service';

@Injectable({ providedIn: 'root' })
export class OnboardingRecoveryService {
  private onboardingService = inject(OnboardingService);
  private onboardingApiService = inject(OnboardingApiService);

  public recoverFromAccountFlow(legalEntityDto: LegalEntityDto): Observable<LegalEntityDto> {
    return this.onboardingService.processClaimFlow(of(legalEntityDto), true);
  }

  public recoverFromMembershipsFlow(account: Account): Observable<LegalEntityDto> {
    return this.onboardingApiService.createMembership(account).pipe(
      switchMap(() => this.onboardingApiService.findAccountForRecovery(account.id)),
      switchMap((accountDto) =>
        this.onboardingApiService
          .createWallet(accountDto.id)
          .pipe(
            switchMap(() =>
              this.onboardingApiService
                .findLegalEntityForRecovery(accountDto.legalEntityId)
                .pipe(
                  switchMap((legalEntity) =>
                    this.onboardingService.processCompanyProfileFlow(legalEntity)
                  )
                )
            )
          )
      )
    );
  }

  // If wallet creation failed, we have separate effects that handle
  // wallet and company profile + location creation.
  public recoverFromWalletFlow(accountId: string): Observable<WalletDto> {
    return this.onboardingApiService.createWallet(toUUID(accountId));
  }

  public recoverFromCompanyFlow(legalEntityId: string): Observable<LegalEntityDto> {
    return this.onboardingApiService
      .findLegalEntityForRecovery(toUUID(legalEntityId))
      .pipe(
        switchMap((legalEntityDto) =>
          this.onboardingService.processCompanyProfileFlow(legalEntityDto)
        )
      );
  }

  public recoverFromLocationFlow(companyProfile: CompanyProfile): Observable<LocationDto> {
    const legalEntityId = companyProfile.legalEntityId as string;
    return this.onboardingApiService.findLegalEntityForRecovery(toUUID(legalEntityId)).pipe(
      switchMap((legalEntity) =>
        this.onboardingApiService.createLocation(legalEntity, companyProfile).pipe(
          switchMap((location) =>
            this.onboardingApiService
              .patchCompanyProfileMainLocation(companyProfile, location.id)
              .pipe(
                map(() => location),
                // The location exists either way, so a failed link must not force the user out.
                catchError(() => of(location))
              )
          )
        )
      )
    );
  }
}
